import { onBeforeUnmount, onMounted, type Ref } from "vue";

/**
 * Chromium can fail to repaint `position: fixed` descendants (e.g. a report
 * footer) after repeated print-preview cycles, especially when combined with
 * a `visibility` toggle used to isolate printable content. Forcing a reflow
 * on the printable root right before printing fixes descendants in one place
 * instead of requiring every fixed-position child to work around it itself.
 */
export function usePrintReflowFix(target: Ref<HTMLElement | undefined | null>) {
  const forceReflow = () => {
    const el = target.value;

    if (!el) return;

    el.style.display = "none";

    // Reading a layout property here is load-bearing: it forces Chromium to
    // flush the pending style recalc synchronously, so the "none" write
    // above actually lands before we flip display back. Without this read,
    // the browser can coalesce the two writes into a no-op, and position:fixed
    // descendants (e.g. ReportFooter) silently fail to repaint on the next
    // print preview. Confirmed by repro: removing this line reintroduces the
    // vanishing-footer bug on repeated preview open/close cycles.
    // Chromium has known, longstanding unreliability rendering position:fixed
    // elements for print - see https://issues.chromium.org/issues/41062969.
    el.getBoundingClientRect();

    el.style.display = "";
  };

  onMounted(() => window.addEventListener("beforeprint", forceReflow));
  onBeforeUnmount(() => window.removeEventListener("beforeprint", forceReflow));
}
